import React, { type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { getPublicConfig } from "./config";
import { isFacebookIntentPageKind, isInstagramIntentPageKind, isYouTubeIntentPageKind, parseRoute, type PageKind } from "./route";
import type { Locale } from "./i18n";
import type { PublicConfig } from "./types";

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root");

const currentURL = `${window.location.pathname}${window.location.search}`;
const publicConfig = getPublicConfig();
const route = parseRoute(currentURL);
const loaded = await loadRoutePage(route.page, route.locale, publicConfig);

const app = (
  <React.StrictMode>
    <App url={currentURL} publicConfig={publicConfig} routePage={loaded.routePage} footerNote={loaded.footerNote} />
  </React.StrictMode>
);

if (root.childElementCount > 0) {
  window.requestAnimationFrame(() => {
    // Some security extensions annotate every div during their first page
    // scan. Clean that non-app attribute after the scan and before hydration.
    root.removeAttribute("bis_skin_checked");
    root.querySelectorAll("[bis_skin_checked]").forEach((element) => element.removeAttribute("bis_skin_checked"));
    ReactDOM.hydrateRoot(root, app);
  });
} else {
  ReactDOM.createRoot(root).render(app);
}

interface LoadedRoutePage {
  routePage?: ReactNode;
  footerNote?: string;
}

async function loadRoutePage(page: PageKind, locale: Locale, publicConfig: PublicConfig): Promise<LoadedRoutePage> {
  if (isYouTubeIntentPageKind(page)) {
    const [{ YouTubeDownloaderPage }, { getYouTubeCopy }] = await Promise.all([import("./YouTubeDownloaderPage"), import("./youtubeI18n")]);
    return { routePage: <YouTubeDownloaderPage locale={locale} intentPage={page} />, footerNote: getYouTubeCopy(locale).footerNote };
  }
  if (isInstagramIntentPageKind(page)) {
    const [{ InstagramDownloaderPage }, { getInstagramCopy }] = await Promise.all([import("./InstagramDownloaderPage"), import("./instagramI18n")]);
    return { routePage: <InstagramDownloaderPage locale={locale} adsEnabled={publicConfig.adsEnabled} intentPage={page} />, footerNote: getInstagramCopy(locale).footerNote };
  }
  if (isFacebookIntentPageKind(page)) {
    const [{ FacebookDownloaderPage }, { getFacebookCopy }] = await Promise.all([import("./FacebookDownloaderPage"), import("./facebookI18n")]);
    return { routePage: <FacebookDownloaderPage locale={locale} adsEnabled={publicConfig.adsEnabled} intentPage={page} />, footerNote: getFacebookCopy(locale).footerNote };
  }
  if (page === "twitter-gif-downloader" && publicConfig.xEnabled) {
    const [{ XDownloaderPage }, { getXCopy }] = await Promise.all([import("./XDownloaderPage"), import("./xI18n")]);
    return { routePage: <XDownloaderPage locale={locale} intentPage={page} />, footerNote: getXCopy(locale).footerNote };
  }
  if (page === "instagram-downloader") {
    const [{ InstagramDownloaderPage }, { getInstagramCopy }] = await Promise.all([import("./InstagramDownloaderPage"), import("./instagramI18n")]);
    return { routePage: <InstagramDownloaderPage locale={locale} adsEnabled={publicConfig.adsEnabled} />, footerNote: getInstagramCopy(locale).footerNote };
  }
  if (page === "youtube-downloader") {
    const [{ YouTubeDownloaderPage }, { getYouTubeCopy }] = await Promise.all([import("./YouTubeDownloaderPage"), import("./youtubeI18n")]);
    return { routePage: <YouTubeDownloaderPage locale={locale} />, footerNote: getYouTubeCopy(locale).footerNote };
  }
  if (page === "facebook-downloader") {
    const [{ FacebookDownloaderPage }, { getFacebookCopy }] = await Promise.all([import("./FacebookDownloaderPage"), import("./facebookI18n")]);
    return { routePage: <FacebookDownloaderPage locale={locale} adsEnabled={publicConfig.adsEnabled} />, footerNote: getFacebookCopy(locale).footerNote };
  }
  if (page === "tiktok-downloader" && publicConfig.tiktokEnabled) {
    const [{ TikTokDownloaderPage }, { getTikTokCopy }] = await Promise.all([import("./TikTokDownloaderPage"), import("./tiktokI18n")]);
    return { routePage: <TikTokDownloaderPage locale={locale} />, footerNote: getTikTokCopy(locale).footerNote };
  }
  if (page === "douyin-downloader" && publicConfig.douyinEnabled) {
    const [{ DouyinDownloaderPage }, { getDouyinCopy }] = await Promise.all([import("./DouyinDownloaderPage"), import("./douyinI18n")]);
    return { routePage: <DouyinDownloaderPage locale={locale} />, footerNote: getDouyinCopy(locale).footerNote };
  }
  if (page === "x-downloader" && publicConfig.xEnabled) {
    const [{ XDownloaderPage }, { getXCopy }] = await Promise.all([import("./XDownloaderPage"), import("./xI18n")]);
    return { routePage: <XDownloaderPage locale={locale} />, footerNote: getXCopy(locale).footerNote };
  }
  if (page === "bilibili-downloader" && publicConfig.bilibiliEnabled) {
    const [{ BilibiliDownloaderPage }, { getBilibiliCopy }] = await Promise.all([import("./BilibiliDownloaderPage"), import("./bilibiliI18n")]);
    return { routePage: <BilibiliDownloaderPage locale={locale} />, footerNote: getBilibiliCopy(locale).footerNote };
  }
  if (page === "youtube-download-formats" || page === "download-youtube-videos-android" || page === "download-youtube-videos-iphone" || page === "download-youtube-subtitles") {
    const [{ YouTubeGuidePage }, { getYouTubeCopy }] = await Promise.all([import("./YouTubeGuidePage"), import("./youtubeI18n")]);
    return { routePage: <YouTubeGuidePage locale={locale} id={page} />, footerNote: getYouTubeCopy(locale).footerNote };
  }
  return {};
}
